import { DateInput as ADateInput, useDateInput } from '@ark-ui/solid/date-input';
import { DatePicker as ADatePicker, useDatePicker, useDatePickerContext } from '@ark-ui/solid/date-picker';
import { IconCalendarEvent } from '@bbr/icons/calendar-event-outline';
import { IconCheck } from '@bbr/icons/check-outline';
import { IconChevronLeft } from '@bbr/icons/chevron-left-outline';
import { IconChevronRight } from '@bbr/icons/chevron-right-outline';
import { Index, Show, createContext, createEffect, createSignal, splitProps, useContext } from 'solid-js';
import { createStore } from 'solid-js/store';
import { Portal } from 'solid-js/web';
import { cn, tv } from 'tailwind-variants';
import { Button } from './button.tsx';
import { Field } from './field.tsx';
import type { UseDateInputProps } from '@ark-ui/solid/date-input';
import type { UseDatePickerProps } from '@ark-ui/solid/date-picker';
import type { DateValue } from '@internationalized/date';
import type { ComponentProps, JSXElement, ParentProps } from 'solid-js';
import type { SetStoreFunction, Store } from 'solid-js/store';

type PickerData = { closeOnSelect: boolean };
type PickerStore = [Store<PickerData>, SetStoreFunction<PickerData>];
const PickerContext = createContext<PickerStore>([{ closeOnSelect: false }, () => {}]);

// type Props = Omit<ComponentProps<typeof ADatePicker.Root>, 'defaultValue' | 'min' | 'max'> & {
// 	type?: 'datetime' | 'date';
// 	defaultValue?: Array<DateValue>;
// 	min?: DateValue;
// 	max?: DateValue;
// 	name: string;
// };
type Props = UseDateInputProps &
	Pick<UseDatePickerProps, 'closeOnSelect' | 'outsideDaySelectable'> & { name: string; class?: string };

export function DatePicker(props: ParentProps<Props>) {
	const [picker, rest, input] = splitProps(props, ['closeOnSelect', 'outsideDaySelectable'], ['class', 'children']);
	const datePicker = useDatePicker(() => ({
		min: input.min,
		max: input.max,
		openOnClick: true,
		outsideDaySelectable: true,
		selectionMode: props.selectionMode,
		value: props.value,
		defaultValue: props.defaultValue,
		...picker,
		onValueChange: props.onValueChange,
	}));
	const dateInput = useDateInput(() => ({
		...input,
		value: datePicker().value ?? props.value ?? props.defaultValue,
		onValueChange(details) {
			datePicker().setValue(details.value);
			props.onValueChange?.(details);
		},
	}));

	const [store, setStore] = createStore<PickerData>({
		// oxlint-disable-next-line solid/reactivity
		closeOnSelect: !!props.closeOnSelect,
	});

	createEffect(() => {
		setStore({
			closeOnSelect: props.closeOnSelect,
		});
	});

	return (
		<PickerContext.Provider value={[store, setStore]}>
			<Field name={props.name} class={rest.class}>
				<ADateInput.RootProvider value={dateInput}>
					<ADatePicker.RootProvider
						value={datePicker}
						class={cn('group/field flex w-full flex-col justify-start gap-1', rest.class)}
					>
						{props.children}
					</ADatePicker.RootProvider>
				</ADateInput.RootProvider>
			</Field>
		</PickerContext.Provider>
	);
}

DatePicker.Control = (props: ComponentProps<typeof ADatePicker.Control>) => {
	const ctx = useDatePickerContext();
	const [container, setContainer] = createSignal<HTMLInputElement>();

	createEffect(() => {
		if (ctx()?.open && window.innerWidth < 1000) {
			setTimeout(() => {
				container()?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
			}, 100);
		}
	});

	return (
		<Show when={!ctx().inline} fallback={<Views />}>
			<ADatePicker.Control
				{...props}
				ref={setContainer}
				class="relative flex w-full items-center justify-between gap-x-1 rounded-md border border-zinc-300 bg-white ps-3 pe-0.5 text-lg transition-all outline-none group-hover/field:ring-4 group-hover/field:ring-yellow-400/30 has-focus-visible:ring-4 has-focus-visible:ring-yellow-500/50 has-focus-visible:ring-offset-1 has-focus-visible:ring-offset-zinc-50 has-disabled:bg-zinc-100 has-disabled:text-zinc-500 has-aria-invalid:border-rose-600 has-aria-readonly:cursor-not-allowed has-aria-readonly:bg-zinc-100 has-aria-readonly:text-zinc-500 data-com-onepassword-filled:bg-clip-text motion-safe:has-disabled:active:animate-shake dark:border-zinc-700 dark:bg-black dark:disabled:bg-zinc-800 dark:has-focus-visible:ring-offset-zinc-950 dark:aria-readonly:bg-zinc-900"
			>
				<ADateInput.HiddenInput index={0} />
				<Show when={ctx().selectionMode === 'range'} fallback={<DateInput />}>
					<ADateInput.HiddenInput index={1} />
					<div class="flex w-full min-w-0 shrink grow-0 items-center justify-center gap-1">
						<DateInput class="w-fit min-w-8 shrink grow justify-end py-2 outline-none" index={0} />
						<span class="shrink-0 grow-0">–</span>
						<DateInput class="min-w-8 shrink grow py-2 outline-none" index={1} />
					</div>
				</Show>
				<ADatePicker.Trigger
					asChild={(props) => (
						// @ts-expect-error too complex
						<Button
							{...props()}
							type="button"
							rounding="md"
							appearance="subtle"
							icon={IconCalendarEvent}
							iconPosition="only"
							iconLabel="Select date"
						/>
					)}
				/>
			</ADatePicker.Control>
			<Portal>
				<ADatePicker.Positioner>
					<ADatePicker.Content class="z-50 max-h-(--available-height) min-h-(--reference-height) max-w-(--available-width) min-w-(--reference-width) animate-out overflow-hidden overscroll-contain rounded-lg border border-zinc-300 bg-white/85 p-2 shadow-md backdrop-blur duration-100 slide-out-to-top-25 zoom-out-50 fade-out data-[state=open]:animate-in data-[state=open]:duration-100 data-[state=open]:slide-in-from-top-25 data-[state=open]:zoom-in-50 data-[state=open]:fade-in dark:border-zinc-800 dark:bg-zinc-900/85">
						<Views />
					</ADatePicker.Content>
				</ADatePicker.Positioner>
			</Portal>
		</Show>
	);
};

function DateInput(props: { class?: string; index?: number }) {
	return (
		<ADateInput.SegmentGroup
			class={cn(
				'flex gap-px py-2 *:inline-flex *:min-w-[calc(2ch+0.25rem)] *:items-center *:justify-center *:rounded *:px-0.5 *:tabular-nums *:caret-transparent *:outline-none *:focus:bg-yellow-500 *:focus:text-zinc-950 *:data-placeholder-shown:text-zinc-500 *:focus:data-placeholder-shown:text-zinc-950 *:data-[type=literal]:min-w-0 *:data-[type=literal]:px-0 *:data-[type=literal]:text-zinc-500 *:data-[type=literal]:select-none',
				props.class,
			)}
			index={props.index ?? 0}
		>
			<ADateInput.SegmentContext>{(segment) => <ADateInput.Segment segment={segment} />}</ADateInput.SegmentContext>
		</ADateInput.SegmentGroup>
	);
}

DatePicker.Label = (props: ComponentProps<typeof ADatePicker.Label>) => (
	<ADateInput.Label {...props} class={cn('flex grow gap-1 text-lg font-medium', props.class)} />
);

export function DatePickerField(
	props: ComponentProps<typeof DatePicker> & { name: string; label: JSXElement; description?: JSXElement },
) {
	const [other, rest] = splitProps(props, ['label', 'description']);
	return (
		<DatePicker {...rest}>
			<DatePicker.Label>{other.label}</DatePicker.Label>
			<DatePicker.Control />
			<Field.Errors />
			<Show when={other.description}>{(desc) => <Field.Description>{desc()}</Field.Description>}</Show>
		</DatePicker>
	);
}

function View(props: ComponentProps<typeof ADatePicker.View>) {
	return <ADatePicker.View {...props} class="flex flex-col gap-1" />;
}

function ViewControl() {
	return (
		<ADatePicker.ViewControl class="flex justify-between">
			<ADatePicker.PrevTrigger
				asChild={(props) => (
					// @ts-expect-error too complex
					<Button
						{...props()}
						appearance="subtle"
						rounding="md"
						icon={IconChevronLeft}
						iconPosition="only"
						iconLabel="Previous"
					/>
				)}
			/>
			<ADatePicker.ViewTrigger
				asChild={(props) => (
					// @ts-expect-error too complex
					<Button {...props()} appearance="subtle" rounding="md" width="full" />
				)}
			>
				<ADatePicker.RangeText />
			</ADatePicker.ViewTrigger>
			<ADatePicker.NextTrigger
				asChild={(props) => (
					// @ts-expect-error too complex
					<Button
						{...props()}
						appearance="subtle"
						rounding="md"
						icon={IconChevronRight}
						iconPosition="only"
						iconLabel="Next"
					/>
				)}
			/>
		</ADatePicker.ViewControl>
	);
}

function Cell(props: { value: number | DateValue; label: string | number }) {
	return (
		<ADatePicker.TableCell class="p-px" value={props.value}>
			<ADatePicker.TableCellTrigger
				asChild={(props) => (
					// @ts-expect-error too complex
					<Button {...props()} appearance="subtle" rounding="md" width="full" />
				)}
			>
				{props.label}
			</ADatePicker.TableCellTrigger>
		</ADatePicker.TableCell>
	);
}

function Views() {
	return (
		<>
			<View view="day">
				<ADatePicker.Context>
					{(context) => (
						<>
							<ViewControl />
							<ADatePicker.Table class={tableClass({ view: 'day' })}>
								<ADatePicker.TableHead>
									<ADatePicker.TableRow class="text-sm">
										<Index each={context().weekDays}>
											{(weekDay) => <ADatePicker.TableHeader>{weekDay().short}</ADatePicker.TableHeader>}
										</Index>
									</ADatePicker.TableRow>
								</ADatePicker.TableHead>
								<ADatePicker.TableBody>
									<Index each={context().weeks}>
										{(week) => (
											<ADatePicker.TableRow>
												<Index each={week()}>{(day) => <Cell value={day()} label={day().day} />}</Index>
											</ADatePicker.TableRow>
										)}
									</Index>
								</ADatePicker.TableBody>
							</ADatePicker.Table>
							<Done />
						</>
					)}
				</ADatePicker.Context>
			</View>
			<View view="month">
				<ADatePicker.Context>
					{(context) => (
						<>
							<ViewControl />
							<ADatePicker.Table class={tableClass({ view: 'month' })}>
								<ADatePicker.TableBody>
									<Index each={context().getMonthsGrid({ columns: 4, format: 'short' })}>
										{(months) => (
											<ADatePicker.TableRow>
												<Index each={months()}>{(month) => <Cell value={month().value} label={month().label} />}</Index>
											</ADatePicker.TableRow>
										)}
									</Index>
								</ADatePicker.TableBody>
							</ADatePicker.Table>
						</>
					)}
				</ADatePicker.Context>
				<Done />
			</View>
			<View view="year">
				<ADatePicker.Context>
					{(context) => (
						<>
							<ViewControl />
							<ADatePicker.Table class={tableClass({ view: 'year' })}>
								<ADatePicker.TableBody>
									<Index each={context().getYearsGrid({ columns: 4 })}>
										{(years) => (
											<ADatePicker.TableRow>
												<Index each={years()}>{(year) => <Cell value={year().value} label={year().label} />}</Index>
											</ADatePicker.TableRow>
										)}
									</Index>
								</ADatePicker.TableBody>
							</ADatePicker.Table>
						</>
					)}
				</ADatePicker.Context>
				<Done />
			</View>
		</>
	);
}

const tableClass = tv({
	base: 'table-fixed **:[button]:px-0 **:[button]:font-medium **:[button]:data-disabled:font-normal **:[button]:data-disabled:text-zinc-400 **:[button]:data-in-range:bg-yellow-200 **:[button]:data-range-end:bg-yellow-500 **:[button]:data-range-start:bg-yellow-500 **:[button]:data-selected:bg-yellow-500 **:[button]:data-selected:text-zinc-950 **:[button]:dark:data-disabled:text-zinc-400',
	variants: {
		view: {
			day: '[&_td]:w-1/7 [&_th]:w-1/7',
			month: '[&_td]:w-1/4',
			year: '[&_td]:w-1/4',
		},
	},
});

function Done(props: ParentProps) {
	const ctx = useDatePickerContext();
	const [store] = useContext(PickerContext);

	return (
		<Show when={!store.closeOnSelect}>
			<div class="flex w-full items-end justify-end gap-4">
				{props.children}
				<Show when={!ctx().inline}>
					<Button type="button" onClick={() => ctx().setOpen(false)} appearance="outline">
						<IconCheck /> Done
					</Button>
				</Show>
			</div>
		</Show>
	);
}
